import React, { useState } from "react";
import ScrollModal from "@/components/common/scroll-modal";
import ConfirmationModalBase from "@/components/common/modals/confirmation-modal-base";
import {
  Heading,
  Button,
  Avatar,
  AvatarFallback,
  Icon,
  Switch,
  useToast,
} from "@elaniintc/goes-design-system";
import TextField, { TextAreaField } from "@/components/common/text-field";
import {
  useCreateTag,
  useUpdateTag,
  useDeleteTag,
} from "@/queries/tags/tags.api";
import type { AxiosError } from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { newTagSchema, editTagFormSchema } from "@/schemas/tags";
import type { NewTagFormData, EditTagFormSchemaData } from "@/schemas/tags";
import {
  MAX_FILE_SIZE_BYTES,
  buildFormValues,
  type NewTagModalProps,
} from "./NewTagModal.helpers";
import { useImageUpload } from "./useImageUpload";
import { NewTagImageUpload } from "./NewTagImageUpload";
import { NewTagModalFooter } from "./NewTagModalFooter";

export default function NewTagModal({
  open,
  onClose,
  onSuccess,
  editData,
}: Readonly<NewTagModalProps>) {
  const isEditing = !!editData;
  const createMutation = useCreateTag();
  const updateMutation = useUpdateTag();
  const deleteMutation = useDeleteTag();
  const mutation = isEditing ? updateMutation : createMutation;
  const { showToast } = useToast();

  // Estado para el modal de confirmación de eliminación
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Estado para el modal de confirmación de guardar cambios
  const [isSaveConfirmModalOpen, setIsSaveConfirmModalOpen] = useState(false);

  // Estado para guardar los datos del formulario temporalmente
  const [pendingFormData, setPendingFormData] = useState<
    NewTagFormData | EditTagFormSchemaData | null
  >(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
    setError,
    clearErrors,
    reset,
    watch,
  } = useForm({
    resolver: zodResolver(isEditing ? editTagFormSchema : newTagSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: buildFormValues(editData),
  });

  const description = watch("description");
  const active = watch("active");

  // Use custom hook for image logic
  const {
    image,
    setImage,
    imagePreview,
    setImagePreview,
    invalidImage,
    setInvalidImage,
    isDragging,
    setIsDragging,
    handleImageChange,
    handleDrop,
    handleRemoveImage,
  } = useImageUpload(MAX_FILE_SIZE_BYTES, (file) => {
    setValue("image", file || undefined);
  });

  // Reset form cuando editData cambia
  React.useEffect(() => {
    if (isEditing && editData) {
      const url =
        editData.image_url ||
        editData.files?.[(editData.files.length || 1) - 1]?.url;
      setImagePreview(url || null);
    } else {
      setImagePreview(null);
      setImage(null);
      setInvalidImage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, editData, reset, setImagePreview, setImage]);

  const onSubmit = async (data: NewTagFormData | EditTagFormSchemaData) => {
    if (isEditing) {
      // En modo edición, mostrar modal de confirmación
      setPendingFormData(data);
      setIsSaveConfirmModalOpen(true);
    } else {
      // En modo creación, proceder directamente
      await executeSubmit(data);
    }
  };

  const executeSubmit = async (
    data: NewTagFormData | EditTagFormSchemaData,
  ) => {
    const payload = {
      name: data.name,
      description: data.description,
      active: data.active,
      image: data.image,
    };
    try {
      if (isEditing) {
        await updateMutation.mutateAsync({ id: editData.id, payload });
        showToast({
          title: "Cambios guardados",
          description: "Los cambios se guardaron exitosamente.",
          options: {
            type: "success",
            duration: 4000,
            customIcon: (
              <Avatar size="xs" shape="square">
                <AvatarFallback color="action" shape="square" type={"ghost"}>
                  <Icon type="fas" name="save" />
                </AvatarFallback>
              </Avatar>
            ),
          },
        });
      } else {
        await createMutation.mutateAsync(payload);
      }

      clearErrors();
      handleClose();
      onSuccess();
    } catch (e) {
      console.error("Error al enviar el formulario:", e);
      const error = e as AxiosError<{
        message?: string;
        errors?: { [key: string]: string[] };
      }>;

      // Manejo de error por nombre duplicado
      const apiMessage = error?.response?.data?.message?.toLowerCase?.() ?? "";
      const nameErrorMsg =
        error?.response?.data?.errors?.name?.[0]?.toLowerCase?.() ?? "";

      if (
        apiMessage.includes("ha sido tomado") ||
        nameErrorMsg.includes("ha sido tomado")
      ) {
        setError("name", {
          type: "duplicate",
          message: "El nombre ya está en uso. Usa uno diferente.",
        });
      } else {
        // Toast de error específico para actualización fallida
        if (isEditing) {
          showToast({
            title: "Error al guardar",
            description:
              "Los cambios no pudieron ser guardados. Por favor vuelve a ingresar a la sección e inténtalo de nuevo.",
            options: {
              type: "error",
              duration: 4000,
              customIcon: (
                <Avatar size="xs" shape="square">
                  <AvatarFallback color="danger" shape="square">
                    <Icon type="fas" name="save-times" />
                  </AvatarFallback>
                </Avatar>
              ),
            },
          });
        }

        setError("name", {
          type: "server",
          message:
            error?.message ||
            error?.response?.data?.message ||
            `Error al ${isEditing ? "actualizar" : "crear"} la etiqueta`,
        });
      }
    }
  };

  const handleClose = () => {
    // Reset formulario a estado inicial limpio
    reset({
      name: "",
      description: "",
      active: false,
      image: undefined,
    });

    // Limpiar estados de imagen
    setImage(null);
    setImagePreview(null);
    setInvalidImage(null);

    onClose();
  };

  const confirmSaveChanges = async () => {
    if (pendingFormData) {
      setIsSaveConfirmModalOpen(false);
      await executeSubmit(pendingFormData);
      setPendingFormData(null);
    }
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (editData?.id) {
      deleteMutation.mutate(editData.id, {
        onSuccess: () => {
          showToast({
            title: "Etiqueta eliminada",
            description: "La etiqueta fue eliminada exitosamente.",
            options: {
              type: "error",
              duration: 4000,
              customIcon: (
                <Avatar size="xs" shape="square">
                  <AvatarFallback color="danger" shape="square" type={"ghost"}>
                    <Icon type="fas" name="trash" />
                  </AvatarFallback>
                </Avatar>
              ),
            },
          });
          setIsDeleteModalOpen(false);
          onSuccess();
          handleClose();
        },
        onError: () => {
          showToast({
            title: "Error al eliminar",
            description:
              "La etiqueta no pudo ser eliminada. Por favor inténtalo nuevamente.",
            options: {
              type: "error",
              duration: 4000,
              customIcon: (
                <Avatar size="xs" shape="square">
                  <AvatarFallback color="danger" shape="square">
                    <Icon type="fas" name="exclamation-triangle" />
                  </AvatarFallback>
                </Avatar>
              ),
            },
          });
        },
      });
    }
  };

  return (
    <>
      <ScrollModal
        isOpen={open}
        onClose={handleClose}
        modalContentClassName="w-full max-w-[520px] max-h-[680px] flex flex-col"
        header={
          <div className="flex items-center justify-between px-8 py-3">
            <Heading variant="h5" className="mb-0!">
              {isEditing ? "Editar Etiqueta" : "Nueva Etiqueta"}
            </Heading>
            <Button
              type="button"
              onClick={handleClose}
              variant="quiet"
              aria-label="Cerrar"
              className="w-8 h-8 cursor-pointer"
            >
              <Icon type="far" name="times" className="text-sm text-body" />
            </Button>
          </div>
        }
        footer={
          <NewTagModalFooter
            isEditing={isEditing}
            isPending={mutation.isPending}
            isValid={isValid}
            editData={editData}
            handleDelete={handleDelete}
            handleClose={handleClose}
          />
        }
      >
        <form
          id="tag-form"
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col flex-1 min-h-0 px-4 sm:px-8"
        >
          {/* Contenido scrollable */}
          <div className="flex flex-col gap-6 pb-6">
            {/* Detalles */}
            <div>
              <div className="flex flex-col gap-4">
                <TextField
                  label="Nombre"
                  required
                  maxLength={50}
                  error={errors.name?.message || ""}
                  placeholder="Escribe el nombre de la etiqueta"
                  {...register("name")}
                />
                <TextAreaField
                  label="Descripción"
                  required
                  maxLength={500}
                  placeholder="Escribe la descripción de la etiqueta"
                  rows={3}
                  className="relative text-body-loud"
                  error={errors.description?.message || ""}
                  {...register("description")}
                  count={description?.length || 0}
                  limit={500}
                />

                {/* Status Field */}
                <div className="flex flex-col items-start gap-1 w-full">
                  <p className="flex items-center w-full font-semibold text-sm leading-6 text-body-loud">
                    <span>Estado</span>
                    <span className="font-semibold text-sm leading-6 text-danger ml-1">
                      *
                    </span>
                  </p>
                  <div className="flex items-center gap-2 w-full">
                    <Switch
                      checked={active}
                      onCheckedChange={(checked) => {
                        setValue("active", checked, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }}
                      id="tag-status-switch"
                    />
                    <label
                      htmlFor="tag-status-switch"
                      className="flex-1 text-base leading-7 font-normal cursor-pointer text-body"
                    >
                      {active ? "Activa" : "Inactiva"}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Carga de imagen */}
            <NewTagImageUpload
              imagePreview={imagePreview}
              isDragging={isDragging}
              setIsDragging={setIsDragging}
              handleDrop={handleDrop}
              handleImageChange={handleImageChange}
              handleRemoveImage={handleRemoveImage}
              image={image}
              isEditing={isEditing}
              editData={editData}
              invalidImage={invalidImage}
              imageError={errors.image?.message}
            />
          </div>
        </form>
      </ScrollModal>

      {/* Modal de confirmación para guardar cambios */}
      <ConfirmationModalBase
        isOpen={isSaveConfirmModalOpen}
        onClose={() => {
          setIsSaveConfirmModalOpen(false);
          setPendingFormData(null);
        }}
        onAccept={confirmSaveChanges}
        title="Guardar cambios"
        description="¿Estás seguro de que deseas guardar los cambios realizados?"
        acceptText="Guardar cambios"
        cancelText="Cancelar"
        acceptVariant="default"
        isLoading={updateMutation.isPending}
      />

      {/* Modal de confirmación para eliminar */}
      <ConfirmationModalBase
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onAccept={confirmDelete}
        title="Eliminar etiqueta"
        description={`¿Estás seguro de que deseas eliminar la etiqueta "${editData?.name}"? Esta acción no se puede deshacer.`}
        acceptText="Eliminar"
        cancelText="Cancelar"
        acceptVariant="danger-primary"
        isLoading={deleteMutation.isPending}
      />
    </>
  );
}
